import React from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, AlertCircle, Info, CheckCircle } from 'lucide-react'
import { getSeverityColor } from '../config/theme'
import './AlertsPanel.css'

function AlertsPanel({ alerts, onAcknowledge }) {
  const navigate = useNavigate()
  
  /**
   * Navigate to patient detail page when patient name is clicked
   */
  const handlePatientClick = (patientId, event) => {
    event.stopPropagation()
    if (patientId) {
      navigate(`/patients/${patientId}`)
    }
  }
  const getSeverityIcon = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
      case 'high':
        return <AlertTriangle size={18} />
      case 'medium':
        return <AlertCircle size={18} />
      case 'low':
        return <Info size={18} />
      default:
        return <AlertCircle size={18} />
    }
  }

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown time'
    const date = new Date(timestamp)
    const now = new Date()
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60))
    
    if (diffHours < 1) return 'Just now'
    if (diffHours < 24) return `${diffHours}h ago`
    return date.toLocaleDateString()
  }

  if (!alerts || alerts.length === 0) {
    return (
      <div className="alerts-panel">
        <div className="alerts-panel-header">
          <h3>Active Alerts</h3>
          <span className="alert-count">0</span>
        </div>
        <div className="alerts-empty">
          <CheckCircle size={48} className="empty-icon" />
          <p>No active alerts</p>
          <span className="empty-subtitle">All patients are stable</span>
        </div>
      </div>
    )
  }

  return (
    <div className="alerts-panel">
      <div className="alerts-panel-header">
        <h3>Active Alerts</h3>
        <span className="alert-count">{alerts.length}</span>
      </div>

      <div className="alerts-list">
        {alerts.map((alert, index) => {
          const severityColor = getSeverityColor(alert.severity)
          
          return (
            <div 
              key={alert.alert_id || index}
              className="alert-item"
              style={{ borderLeftColor: severityColor }}
            >
              <div className="alert-header">
                <div className="alert-icon" style={{ color: severityColor }}>
                  {getSeverityIcon(alert.severity)}
                </div>
                <div className="alert-content">
                  <div className="alert-patient">
                    <span
                      className="alert-patient-link"
                      onClick={(e) => handlePatientClick(alert.patient_id, e)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && handlePatientClick(alert.patient_id, e)}
                    >
                      {alert.patient_name}
                    </span>
                    <span className="alert-severity" style={{ color: severityColor }}>
                      {alert.severity?.toUpperCase()}
                    </span>
                  </div>
                  <div className="alert-message">{alert.message}</div>
                  <div className="alert-meta">
                    <span className="alert-type">{alert.type?.replace(/_/g, ' ')}</span>
                    <span className="alert-time">{formatTimestamp(alert.timestamp)}</span>
                  </div>
                </div>
              </div>

              {!alert.acknowledged && onAcknowledge && (
                <button
                  className="alert-acknowledge-btn"
                  onClick={(e) => {
                    e.stopPropagation()
                    onAcknowledge(alert.alert_id)
                  }}
                >
                  Acknowledge
                </button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default AlertsPanel